import { Box, Link, NativeSelect, Spinner, Tabs, Text } from '@chakra-ui/react';
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFinancialStatements } from '../hooks/useFinancialStatements';
import {
    DEFAULT_FINANCIAL_STATEMENT,
    FINANCIAL_STATEMENTS,
    getFinancialStatementByLabel,
} from '../pages/stock/financialStatementRoutes';
import './FinancialStatements.css';

const YEAR_COLUMN_COLORS = ['#f8fafc', '#eef6ff', '#f6f8eb', '#fff7ed'];

export function FinancialStatementTable({ title, data }) {
    if (!data || data.length === 0) {
        return <p>No data available for {title}</p>;
    }

    const yearKeys = [...new Set(
        data.flatMap((row) => Object.keys(row).filter((key) => key !== 'Metric'))
    )];

    const sortedYears = yearKeys.sort((a, b) => {
        const yearA = new Date(a).getFullYear();
        const yearB = new Date(b).getFullYear();
        if (isNaN(yearA) && isNaN(yearB)) return 0;
        if (isNaN(yearA)) return 1;
        if (isNaN(yearB)) return -1;
        return yearB - yearA;
    });

    return (
        <div className="financial-statement-table-scroll">
            <table
                className="financial-statement-table"
                style={{
                    minWidth: `${Math.max(640, 220 + sortedYears.length * 140)}px`,
                }}
            >
                <thead>
                    <tr>
                        <th
                            style={{
                                padding: '12px 16px',
                                backgroundColor: '#1f2937',
                                color: '#ffffff',
                                borderBottom: '1px solid #d7dee7',
                                position: 'sticky',
                                left: 0,
                                zIndex: 2,
                            }}
                        >
                            Metric
                        </th>
                        {sortedYears.map((yearLabel, index) => (
                            <th
                                key={yearLabel}
                                style={{
                                    padding: '12px 16px',
                                    backgroundColor: YEAR_COLUMN_COLORS[index % YEAR_COLUMN_COLORS.length],
                                    borderBottom: '1px solid #d7dee7',
                                    borderLeft: '1px solid #d7dee7',
                                    whiteSpace: 'nowrap',
                                    minWidth: '140px',
                                }}
                            >
                                {yearLabel}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, index) => (
                        <tr
                            key={`${row.Metric}-${index}`}
                            style={{
                                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                            }}
                        >
                            <td
                                style={{
                                    padding: '11px 16px',
                                    fontWeight: 600,
                                    borderBottom: '1px solid #e5e7eb',
                                    position: 'sticky',
                                    left: 0,
                                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                                    zIndex: 1,
                                }}
                            >
                                {row.Metric}
                            </td>
                            {sortedYears.map((yearLabel, idx) => (
                                <td
                                    key={yearLabel}
                                    style={{
                                        padding: '11px 16px',
                                        borderBottom: '1px solid #e5e7eb',
                                        borderLeft: '1px solid #e5e7eb',
                                        backgroundColor: YEAR_COLUMN_COLORS[idx % YEAR_COLUMN_COLORS.length],
                                    }}
                                >
                                    {row[yearLabel] ?? ''}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function FinancialStatements({ ticker, selectedStatement }) {
    const [localStatement, setLocalStatement] = useState(selectedStatement ?? DEFAULT_FINANCIAL_STATEMENT);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const isRouteDrivenStatement = Boolean(selectedStatement);
    const activeStatement = selectedStatement ?? localStatement;
    const requestedYear = searchParams.get('year');
    const {
        loading,
        financialData,
        availableYears,
        statementSourceUrls,
    } = useFinancialStatements(ticker, requestedYear, setSearchParams);
    const activeSourceUrl = statementSourceUrls[activeStatement.titleKey];

    // Render the tables only after the data is loaded
    return (
        <div style={{ position: 'relative', height: '80vh', width: '100%' }}>
            {loading ? (
                <>
                    <Spinner size='xl' color='green.500' />
                    <br />
                    <Text>Fetching financial data…</Text>
                    <br />
                </>
            ) : (
                <>
                    <h1>Financial Statements for {ticker.toUpperCase()}</h1>

                    {/* Buttons to switch between statements */}
                    <Tabs.Root
                        value={activeStatement.label}
                        onValueChange={({ value }) => {
                            const nextStatement = getFinancialStatementByLabel(value);
                            if (isRouteDrivenStatement) {
                                const nextSearch = searchParams.toString();
                                navigate(`/stock/${ticker.toLowerCase()}/financials/${nextStatement.slug}${nextSearch ? `?${nextSearch}` : ''}`);
                                return;
                            }

                            setLocalStatement(nextStatement);
                        }}
                        style={{ marginBottom: '20px' }}
                    >
                        <Tabs.List>
                            {FINANCIAL_STATEMENTS.map((statement) => (
                                <Tabs.Trigger key={statement.slug} value={statement.label}>
                                    {statement.label}
                                </Tabs.Trigger>
                            ))}
                        </Tabs.List>
                    </Tabs.Root>

                    {availableYears.length > 0 && (
                        <Box mb={4} display="flex" alignItems="center" flexWrap="wrap" gap="3" rowGap="2">
                            <Text fontSize="sm" fontWeight="medium">Year</Text>
                            <NativeSelect.Root size="sm" w="120px">
                                <NativeSelect.Field
                                    aria-label="Select financial year"
                                    value={requestedYear ?? ''}
                                    onChange={(event) => {
                                        const nextYear = event.target.value;
                                        setSearchParams((currentParams) => {
                                            const nextParams = new URLSearchParams(currentParams);
                                            if (nextYear) {
                                                nextParams.set('year', nextYear);
                                            } else {
                                                nextParams.delete('year');
                                            }
                                            return nextParams;
                                        });
                                    }}
                                >
                                    {availableYears.map((year) => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))}
                                </NativeSelect.Field>
                                <NativeSelect.Indicator />
                            </NativeSelect.Root>
                        </Box>
                    )}

                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        gap="3"
                        mt={2}
                        mb={2}
                    >
                        <span style={{ fontWeight: 'bold' }}>{financialData.titles[activeStatement.titleKey]}</span>
                        {activeSourceUrl && (
                            <Link
                                href={activeSourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                fontSize="sm"
                                fontWeight="semibold"
                                color="blue.600"
                            >
                                See source
                            </Link>
                        )}
                    </Box>

                    {/* Conditionally render the selected financial statement */}
                    <FinancialStatementTable
                        title={activeStatement.label}
                        data={financialData[activeStatement.dataKey]}
                    />
                </>
            )}

            <div style={{padding: '20px'}}></div>
        </div>
    );
}

export default FinancialStatements;
